import { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { trackTakeTourAction } from '../../../analytics/mixpanel';
import NoteUI from '../../../CommonComponents/UI/NoteUI/NoteUI';
import {
  ACTIVE_TAB_MAPPING,
  LOADING,
  WORKFLOW_INTRO_MODAL,
} from '../../../Constants/commonConstants';
import { useLocalStorage } from '../../../utils/hooks/useLocalStorage/useLocalStorage';
import { TAKE_TOUR_TYPES } from '../constants/constants';
import { useTakeTourContext } from '../Context/useTakeTourContext';

export const useTakeTour = () => {
  const {
    startTour,
    isOpen,
    currentStep,
    data,
    steps,
    tourType,
    closeTour,
    nextStep,
    prevStep,
    stepInProgress,
    stepInProgressState,
    dropdownAndMiscStates,
    setDropdownAndMiscStates,
    tourStatusesData,
    tourStatusesLoading,
    updateUserTourStatusLoading,
    getTourStatus,
    hasUserTakenTour,
    tourTakenForSession,
  } = useTakeTourContext();
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const [workflowIntroModalObj] = useLocalStorage(WORKFLOW_INTRO_MODAL);

  const uploadStatus = useSelector((state) => state.docChatData.uploadStatus);

  // Tour step definitions
  const DOCCHAT_DASHBOARD_STEPS = useMemo(
    () => [
      {
        disableBeacon: true,
        target: 'body',
        placement: 'center',
        title: (
          <div>
            Welcome to <span className='color-primary'>Mike DocChat!</span>
          </div>
        ),
        content:
          'Review your legal documents with AI powered chat and workflows. Upload any document to get started!',
        data: {
          styles: {
            width: '500px',
          },
        },
        prevButtonProps: {
          onClick: () => {
            prevStep();
          },
        },
        nextButtonProps: {
          onClick: () => {
            nextStep();
          },
        },
      },
      {
        disableBeacon: true,
        target: '#upload-docchat',
        title: (
          <div>
            <span className='color-primary'>Upload</span> your contracts or other legal documents to
            get started
          </div>
        ),
        content: (
          <>
            <div>Click or drag-and-drop to upload PDFs or Word files (including scanned PDFs).</div>
            <div>You can upload up to 50 documents at once.</div>
            <NoteUI
              note={
                <>
                  If you upload multiple documents, you’ll see two options: <b>Upload Separately</b>{' '}
                  (upload each file individually) or <b>Create a Folder and Start</b> (upload all
                  files under one folder).
                </>
              }
            />
          </>
        ),
        data: {
          styles: {
            width: '800px',
          },
        },
        prevButtonProps: {
          onClick: () => {
            prevStep();
          },
        },
        nextButtonProps: {
          onClick: () => {
            nextStep();
          },
        },
      },
      {
        disableBeacon: true,
        target: '#ask-docchat-input',
        title: (
          <>
            <span className='color-primary'>Ask DocChat</span> Directly
          </>
        ),
        content: (
          <div>
            Type your query in <b>Ask DocChat</b> and then click on{' '}
            <b className='color-primary'>Start DocChat</b>.
          </div>
        ),
        data: {
          styles: {
            width: '700px',
          },
        },
        prevButtonProps: {
          onClick: () => {
            prevStep();
          },
        },
        nextButtonProps: {
          onClick: () => {
            nextStep();
          },
        },
      },
      {
        disableBeacon: true,
        target: '#dashboard-mikelegal-magic-button',
        title: (
          <>
            Use <span className='color-primary'>MikeLegal’s Workflows</span>
          </>
        ),
        content: (
          <>
            <div>
              These are preset prompts created by us that extract structured data from legal
              documents in excel format.
            </div>
            <div>
              Click <b className='app-gradient-text'>MikeLegal’s Magic</b> to open the dropdown,
              then search or choose an action to run a ready-made workflow.
            </div>
          </>
        ),
        data: {
          styles: {
            width: '700px',
          },
        },
        prevButtonProps: {
          onClick: () => {
            prevStep();
          },
        },
        nextButtonProps: {
          onClick: () => {
            nextStep();
          },
        },
      },
      {
        disableBeacon: true,
        target: '#dashboard-your-workflows-button',
        title: (
          <>
            Use <span className='color-primary'>Your Workflows</span>
          </>
        ),
        content: (
          <>
            <div>
              Your Workflows lets you run custom extraction prompts and export user-defined fields
              in Excel format.
            </div>
            <div>
              Click <b className='color-primary'>Your Workflows</b> to open the dropdown, then
              search or choose a saved workflow.
            </div>
          </>
        ),
        data: {
          styles: {
            width: '700px',
          },
        },
        prevButtonProps: {
          onClick: () => {
            prevStep();
          },
        },
        nextButtonProps: {
          onClick: () => {
            setDropdownAndMiscStates({
              filePickerDocChatSelectedOption: 'Your Workflows',
              filePickerDocChatSelectedOptionContainerOpen: true,
              presetsState: {},
            });
            setTimeout(() => {
              nextStep();
            }, 200);
          },
        },
      },
      {
        spotlightPadding: 5,
        disableBeacon: true,
        target: '#workflow-select-dropdown-render',
        placement: 'left',
        title: <b className='color-primary'>Create a New Workflow</b>,
        content: (
          <div>
            In this dropdown, click <b className='color-primary'>Create a New Workflow</b> to set up
            a new workflow!
          </div>
        ),
        description:
          'Our AI-powered features that help you analyze and understand your documents better.',
        data: {
          styles: {
            width: '400px',
          },
        },
        prevButtonProps: {
          onClick: () => {
            setDropdownAndMiscStates({});
            prevStep();
          },
        },
        nextButtonProps: {
          onClick: () => {
            setDropdownAndMiscStates({});
            navigate('/settings/docchat/workflow/create');
            nextStep();
          },
        },
      },
      {
        disableBeacon: true,
        target: '.workflow-form',
        title: (
          <>
            <span className='color-primary'>Setup and Save</span> Your Workflow
          </>
        ),
        content: (
          <>
            <div>
              Enter a <b>workflow name</b>, <b>description</b>, and <b>prompt details</b>. Then,
              click <b className='color-primary'>Save</b>.
            </div>
            <div>
              Need help with prompt? Hover over the help icon next to each field to view guidance.
            </div>
            <NoteUI
              note={
                <>
                  You can also reach this page directly via
                  <b> Profile → Settings → Workflows → Create a New Workflow</b>
                </>
              }
            />
          </>
        ),
        data: {
          styles: {
            width: '400px',
          },
          adjustWorkflowFormWidth: true,
        },
        placement: 'right-start',
        prevButtonProps: {
          onClick: () => {
            navigate('/docchat');
            setDropdownAndMiscStates({
              filePickerDocChatSelectedOption: 'Your Workflows',
              filePickerDocChatSelectedOptionContainerOpen: true,
              presetsState: {},
            });
            setTimeout(() => {
              prevStep();
            }, 200);
          },
        },
        nextButtonProps: {
          onClick: () => {
            nextStep();
          },
        },
      },
      {
        disableBeacon: true,
        target: '#workflow-setting',
        title: (
          <>
            <span className='color-primary'>View</span> your existing workflows
          </>
        ),
        content: (
          <div>
            This tab lists all workflows you’ve already created. You can <b>search</b>, <b>edit</b>,
            or <b>delete</b> workflows here. You can also <b>create a New Workflow</b> here.
          </div>
        ),
        placement: 'right',
        prevButtonProps: {
          onClick: () => {
            prevStep();
          },
        },
        nextButtonProps: {
          onClick: () => {
            // closeTour();
          },
        },
      },
    ],
    [navigate, nextStep, prevStep, setDropdownAndMiscStates]
  );

  const VIEWER_DOCCHAT_STEPS = useMemo(
    () => [
      {
        disableBeacon: true,
        target: '#viewer-docChat-chat-input',
        title: (
          <>
            <span className='color-primary'>Ask DocChat</span> Anything!
          </>
        ),
        content: (
          <div>
            Enter your query in the <b>Chat Input</b> to get instant responses from your uploaded
            documents.
          </div>
        ),
        placement: 'top',
        prevButtonProps: {
          onClick: () => {
            prevStep();
          },
        },
        nextButtonProps: {
          onClick: () => {
            nextStep();
          },
        },
      },
      {
        disableBeacon: true,
        target: '#chatbot-action-buttons',
        title: (
          <>
            <span className='color-primary'>Export</span> or{' '}
            <span className='color-primary'>Clear Chat</span>
          </>
        ),
        content: (
          <>
            <div>
              Use <b>Export</b> to download the conversation in Word or PDF.
            </div>
            <div>
              Use <b>Clear Chat</b> to clear entire chat history.
            </div>
          </>
        ),
        prevButtonProps: {
          onClick: () => {
            prevStep();
          },
        },
        nextButtonProps: {
          onClick: () => {
            nextStep();
          },
        },
      },
      {
        disableBeacon: true,
        target: '.workflow-tab',
        title: (
          <>
            Open the <span className='color-primary'>Workflow</span> tab
          </>
        ),
        content:
          'Access your most-used workflows from the Chat Window to run extraction on uploaded documents and download results in Excel format.',
        placement: 'bottom',
        prevButtonProps: {
          onClick: () => {
            prevStep();
          },
        },
        nextButtonProps: {
          onClick: () => {
            setDropdownAndMiscStates({
              chatBoxActiveTab: ACTIVE_TAB_MAPPING.presets,
              workflowTabFirstSelectValue: 'workflow',
            });
            nextStep();
          },
        },
      },
      {
        target: '#chat-box-workflow-select',
        title: (
          <>
            Choose a <span className='color-primary'>workflow</span> from the dropdown
          </>
        ),
        content:
          'Click to open the dropdown, then search or choose an option to run extraction and download results in Excel format.',
        placement: 'top',
        prevButtonProps: {
          onClick: () => {
            setDropdownAndMiscStates({
              chatBoxActiveTab: ACTIVE_TAB_MAPPING.chat,
            });
            prevStep();
          },
        },
        nextButtonProps: {
          onClick: () => {
            setDropdownAndMiscStates((prev) => ({
              ...prev,
              appSelectPillOpen: true,
            }));
            nextStep();
          },
        },
      },
      {
        spotlightPadding: 0,
        target: '.workflow-tab-first-select',
        title: (
          <>
            Switch between <span className='color-primary'>workflow types</span>
          </>
        ),
        content: (
          <div>
            Use this switch to choose between <b className='app-gradient-text'>MikeLegal’s Magic</b>{' '}
            (preset workflows) and <b className='color-primary'>Your Workflows</b> (custom
            workflows).
          </div>
        ),
        placement: 'top',
        prevButtonProps: {
          onClick: () => {
            setDropdownAndMiscStates((prev) => ({
              ...prev,
              appSelectPillOpen: false,
            }));
            prevStep();
          },
        },
        nextButtonProps: {
          onClick: () => {
            setDropdownAndMiscStates({});
            nextStep();
          },
        },
      },
      {
        target: '.saved-docchat-search-input',
        title: (
          <>
            Find your <span className='color-primary'>uploaded documents</span> and{' '}
            <span className='color-primary'>folders</span>
          </>
        ),
        content: 'Search for previously uploaded documents or folders here.',
        placement: 'right',
        prevButtonProps: {
          onClick: () => {
            setDropdownAndMiscStates({
              workflowTabFirstSelectValue: 'workflow',
              chatBoxActiveTab: ACTIVE_TAB_MAPPING.presets,
              appSelectPillOpen: true,
            });
            setTimeout(() => {
              prevStep();
            }, 200);
          },
        },
        nextButtonProps: {
          onClick: () => {
            nextStep();
          },
        },
      },
      {
        target: '.list-more-button',
        title: (
          <>
            Manage <span className='color-primary'>documents</span> with the menu
          </>
        ),
        content: (
          <div>
            Click the <b>three-dot</b> menu next to any item to <b>Rename, Clear Chat, Delete</b> or{' '}
            <b>Move</b>.
          </div>
        ),
        placement: 'right',
        prevButtonProps: {
          onClick: () => {
            prevStep();
          },
        },
        nextButtonProps: {
          onClick: () => {
            setDropdownAndMiscStates({
              profileDropdownOpen: true,
            });
            // next step after dropdown is open
            setTimeout(() => {
              nextStep();
            }, 200);
          },
        },
      },
      {
        spotlightPadding: 0,
        target: '.profile-dropdown-overlay',
        title: (
          <>
            Access <span className='color-primary'>Profile</span> &{' '}
            <span className='color-primary'>Settings</span>
          </>
        ),
        content: (
          <div>
            Open the <b>Profile dropdown</b> to{' '}
            <b>switch products, manage settings, access Help Center (FAQs)</b> or <b>log out.</b>
          </div>
        ),
        placement: 'left',
        prevButtonProps: {
          onClick: () => {
            setDropdownAndMiscStates({
              profileDropdownOpen: false,
            });
            prevStep();
          },
        },
        nextButtonProps: {
          onClick: () => {
            setDropdownAndMiscStates({});
            nextStep();
          },
        },
      },
      {
        target: '#nav-docchat-quota',
        title: (
          <>
            Check your <span className='color-primary'>document quota</span>
          </>
        ),
        content: (
          <div>
            <div>This shows how many document uploads are remaining in your current quota.</div>
            <div>
              <b>Contact us</b> if you’d like to increase or refill your quota.
            </div>
          </div>
        ),
        prevButtonProps: {
          onClick: () => {
            setDropdownAndMiscStates({
              profileDropdownOpen: true,
            });
            prevStep();
          },
        },
        nextButtonProps: {
          onClick: () => {
            console.info('last step');
          },
        },
      },
    ],
    [nextStep, prevStep, setDropdownAndMiscStates]
  );

  const startDocChatDashboardTour = useCallback(
    (manualTrigger = false) => {
      if (uploadStatus === LOADING) return;
      trackTakeTourAction({
        takeTourType: TAKE_TOUR_TYPES.DOCCHAT_HOMEPAGE,
        action: manualTrigger ? 'take tour button click' : 'auto trigger',
      });
      startTour(TAKE_TOUR_TYPES.DOCCHAT_HOMEPAGE, DOCCHAT_DASHBOARD_STEPS, {
        manualTrigger,
        currentUrl: '/docchat',
      });
    },
    [uploadStatus, startTour, DOCCHAT_DASHBOARD_STEPS]
  );

  const startViewerDocChatTour = useCallback(
    (manualTrigger = false) => {
      trackTakeTourAction({
        takeTourType: TAKE_TOUR_TYPES.DOCCHAT_CHAT_VIEWER,
        action: manualTrigger ? 'take tour button click' : 'auto trigger',
      });
      setDropdownAndMiscStates({
        chatBoxActiveTab: ACTIVE_TAB_MAPPING.chat,
      });
      const currentUrl = window.location.pathname + window.location.search;

      setSearchParams({ uuid: 'document-id-1' });
      startTour(TAKE_TOUR_TYPES.DOCCHAT_CHAT_VIEWER, VIEWER_DOCCHAT_STEPS, {
        currentUrl,
        manualTrigger,
      });
    },
    [setDropdownAndMiscStates, setSearchParams, startTour, VIEWER_DOCCHAT_STEPS]
  );

  const currentDate = new Date();
  const docChatDashboardTourStatus = getTourStatus(TAKE_TOUR_TYPES.DOCCHAT_HOMEPAGE);
  const docChatDashboardTourTaken = hasUserTakenTour(TAKE_TOUR_TYPES.DOCCHAT_HOMEPAGE);
  const shouldAutoTriggerDocChatDashboard =
    !tourTakenForSession[TAKE_TOUR_TYPES.DOCCHAT_HOMEPAGE] &&
    !updateUserTourStatusLoading &&
    !tourStatusesLoading &&
    (docChatDashboardTourStatus === undefined ||
      (!docChatDashboardTourTaken &&
        new Date(docChatDashboardTourStatus.reminderDate) <= currentDate));

  const viewerDocChatTourStatus = getTourStatus(TAKE_TOUR_TYPES.DOCCHAT_CHAT_VIEWER);
  const viewerDocChatTourTaken = hasUserTakenTour(TAKE_TOUR_TYPES.DOCCHAT_CHAT_VIEWER);
  const shouldAutoTriggerViewDocChatTour =
    !tourTakenForSession[TAKE_TOUR_TYPES.DOCCHAT_CHAT_VIEWER] &&
    !updateUserTourStatusLoading &&
    !tourStatusesLoading &&
    (viewerDocChatTourStatus === undefined ||
      (!viewerDocChatTourTaken && new Date(viewerDocChatTourStatus.reminderDate) <= currentDate));

  // Check for reminder dates and start tours automatically
  useEffect(() => {
    if (!tourStatusesData || isOpen) return;
    // auto trigger take tour
    if (shouldAutoTriggerDocChatDashboard) {
      // Check if we're on the correct page before starting the tour
      if (window.location.pathname === '/docchat') {
        setTimeout(() => {
          startDocChatDashboardTour();
        }, 1000); // Small delay to ensure page is loaded
      }
    }

    const userId = localStorage.getItem('user_id');
    const workflowIntroModalWillShow = (workflowIntroModalObj || {})[userId] === undefined;
    // auto trigger take tour
    // if workflow intro modal will show, the "ok" button of the modal will trigger the tour
    if (workflowIntroModalWillShow !== true && shouldAutoTriggerViewDocChatTour) {
      // Check if we're on the correct page before starting the tour
      if (window.location.pathname.startsWith('/viewer-docchat')) {
        setTimeout(() => {
          startViewerDocChatTour();
        }, 1000); // Small delay to ensure page is loaded
      }
    }
  }, [
    tourStatusesData,
    isOpen,
    shouldAutoTriggerDocChatDashboard,
    shouldAutoTriggerViewDocChatTour,
    workflowIntroModalObj,
    startDocChatDashboardTour,
    startViewerDocChatTour,
  ]);

  const getCurrentStepInfo = () => {
    if (!isOpen || !steps.length) return null;
    return steps[currentStep];
  };

  const isLastStep = () => {
    return currentStep === steps.length - 1;
  };

  const isFirstStep = () => {
    return currentStep === 0;
  };

  const getProgress = () => {
    if (!steps.length) return 0;
    return ((currentStep + 1) / steps.length) * 100;
  };

  return {
    dropdownAndMiscStates,
    steps,
    // Tour state
    isOpen,
    data,
    currentStep,
    totalSteps: steps.length,
    tourType,
    currentStepInfo: getCurrentStepInfo(),
    progress: getProgress(),

    // Navigation helpers
    isLastStep: isLastStep(),
    isFirstStep: isFirstStep(),

    // Tour starters
    startDocChatDashboardTour,
    startViewerDocChatTour,

    // Raw context methods (for advanced usage)
    startTour,
    closeTour,
    stepInProgress,
    stepInProgressState,
    shouldAutoTriggerViewDocChatTour,
  };
};
